import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Receipt, Link as LinkIcon, X, MagnifyingGlass, Plus } from '@phosphor-icons/react';
import { useSession } from '@clerk/clerk-react';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';

/** Eén financieel document (offerte / factuur / bestelbon) uit Renovatie. */
export interface RenoFinancialDocument {
  id: number;
  documentNumber: string;
  type: string;
  status: string;
  documentDate: string;
  isCreditNote?: boolean;
  supplier?: { id: number; name: string } | null;
  lines?: { totalExclVat?: number | string; totalInclVat?: number | string }[];
}

interface Props {
  ticketId: number;
  /** Bedragen zoals de backend ze berekende; getoond als samenvatting bovenaan. */
  approvedQuoteAmount?: number;
  invoiceAmount?: number;
  /** Aangeroepen na koppelen/ontkoppelen, zodat het ticket zijn bedragen herlaadt. */
  onChanged?: () => void;
}

const formatAmount = (value: number) =>
  new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR' }).format(value);

const formatDate = (value?: string) =>
  value ? new Date(value).toLocaleDateString('nl-BE') : '—';

/**
 * Bedrag van één document, excl. btw en met het juiste teken.
 *
 * Het teken waarmee een documentregel bewaard is, hangt van het importpad af, dus
 * `Math.abs` + de `isCreditNote`-vlag bepalen het teken — exact zoals de backend en de
 * documentenlijst het doen.
 */
export const renoDocumentAmount = (document: RenoFinancialDocument): number => {
  const total = (document.lines ?? []).reduce(
    (sum, line) => sum + Math.abs(Number(line.totalInclVat ?? line.totalExclVat ?? 0) || 0),
    0,
  );
  return document.isCreditNote ? -total : total;
};

const TYPE_BADGE: Record<string, string> = {
  Offerte: 'bg-blue-50 text-blue-700 border-blue-200',
  Factuur: 'bg-moss-primary/10 text-moss-primary border-moss-primary/20',
  Bestelbon: 'bg-amber-50 text-amber-800 border-amber-200',
  Contract: 'bg-purple-50 text-purple-700 border-purple-200',
};

/**
 * Financiële documenten van een renovatieticket: offertes, facturen en bestelbonnen
 * uit **Renovatie → Financiële documenten**, gekoppeld aan dit ticket.
 *
 * Zelfde principe als de OPEX-facturen bij een helpdeskticket: het ticket bewaart geen
 * bedragen, maar leidt ze af uit de gekoppelde documenten. Zo kan het goedgekeurde
 * offertebedrag of factuurbedrag op het ticket nooit verouderen tegenover het document.
 *
 * Zoeken naar te koppelen documenten gebeurt **backend-side** en is beperkt tot het
 * gebouw/project van het ticket en tot documenten die nog aan geen ticket hangen.
 */
export const RenoFinancialDocumentsSection: React.FC<Props> = ({
  ticketId,
  approvedQuoteAmount,
  invoiceAmount,
  onChanged,
}) => {
  const { session } = useSession();
  const [documents, setDocuments] = useState<RenoFinancialDocument[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [candidates, setCandidates] = useState<RenoFinancialDocument[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = useCallback(async () => {
    if (!session || !ticketId) return;
    setIsLoading(true);
    try {
      const res = await fetchWithAuth(
        `${API_URL}/reno-helpdesk/tickets/${ticketId}/financial-documents`,
        { method: 'GET' },
        session,
      );
      if (!res.ok) throw new Error('Gekoppelde documenten ophalen mislukt');
      setDocuments(await res.json());
    } catch (error) {
      Sentry.captureException(error, {
        tags: { module: 'reno-helpdesk', action: 'loadFinancialDocuments' },
        extra: { ticketId },
      });
      toast.error('De financiële documenten konden niet geladen worden');
    } finally {
      setIsLoading(false);
    }
  }, [session, ticketId]);

  useEffect(() => { load(); }, [load]);

  // Kandidaten opnieuw zoeken bij het openen en bij elke zoekterm (debounce 300 ms).
  useEffect(() => {
    if (!isPickerOpen || !session) return;
    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        const params = new URLSearchParams();
        if (search.trim()) params.set('search', search.trim());
        const res = await fetchWithAuth(
          `${API_URL}/reno-helpdesk/tickets/${ticketId}/financial-documents/available?${params}`,
          { method: 'GET' },
          session,
        );
        if (!res.ok) throw new Error('Zoeken naar documenten mislukt');
        setCandidates(await res.json());
      } catch (error) {
        Sentry.captureException(error, {
          tags: { module: 'reno-helpdesk', action: 'searchFinancialDocuments' },
          extra: { ticketId, search },
        });
        toast.error('Zoeken naar documenten mislukt');
      } finally {
        setIsSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [isPickerOpen, search, session, ticketId]);

  const link = async (documentId: number) => {
    if (!session) return;
    setBusyId(documentId);
    try {
      const res = await fetchWithAuth(
        `${API_URL}/reno-helpdesk/tickets/${ticketId}/financial-documents`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ documentId }),
        },
        session,
      );
      if (!res.ok) {
        // De backend zegt waarom (bv. al aan een ander ticket gekoppeld).
        const uitleg = await res.json().then((d) => d?.message).catch(() => undefined);
        throw new Error(uitleg || 'Koppelen mislukt');
      }
      toast.success('Document gekoppeld');
      setCandidates((prev) => prev.filter((c) => c.id !== documentId));
      await load();
      onChanged?.();
    } catch (error) {
      Sentry.captureException(error, {
        tags: { module: 'reno-helpdesk', action: 'linkFinancialDocument' },
        extra: { ticketId, documentId },
      });
      toast.error((error as Error).message);
    } finally {
      setBusyId(null);
    }
  };

  const unlink = async (documentId: number) => {
    if (!session) return;
    setBusyId(documentId);
    try {
      const res = await fetchWithAuth(
        `${API_URL}/reno-helpdesk/tickets/${ticketId}/financial-documents/${documentId}`,
        { method: 'DELETE' },
        session,
      );
      if (!res.ok) throw new Error('Koppeling losmaken mislukt');
      toast.success('Koppeling losgemaakt');
      await load();
      onChanged?.();
    } catch (error) {
      Sentry.captureException(error, {
        tags: { module: 'reno-helpdesk', action: 'unlinkFinancialDocument' },
        extra: { ticketId, documentId },
      });
      toast.error((error as Error).message);
    } finally {
      setBusyId(null);
    }
  };

  const totals = useMemo(
    () => ({
      quote: approvedQuoteAmount ?? 0,
      invoice: invoiceAmount ?? 0,
    }),
    [approvedQuoteAmount, invoiceAmount],
  );

  return (
    <div className="border-muted-foreground/20 bg-muted overflow-hidden rounded-lg border">
      <div className="border-neutral-primary/10 flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3">
        <div className="flex items-center gap-2">
          <Receipt size={20} className="text-moss-primary" />
          <h3 className="text-neutral-primary font-semibold">
            Financiële documenten
            {documents.length > 0 && <span className="ml-1">({documents.length})</span>}
          </h3>
        </div>
        <Button
          size="sm"
          onClick={() => setIsPickerOpen((prev) => !prev)}
          className="bg-moss-primary hover:bg-moss-primary/90 h-7 gap-1 px-2 text-xs text-white"
        >
          <LinkIcon size={12} />
          {isPickerOpen ? 'Sluiten' : 'Document koppelen'}
        </Button>
      </div>

      {/* Samenvatting: dezelfde cijfers als de kolommen in de ticketlijst. */}
      <div className="border-neutral-primary/10 grid grid-cols-2 gap-4 border-b px-4 py-3">
        <div>
          <p className="text-neutral-secondary-mid text-xs uppercase">Goedgekeurd offertebedrag</p>
          <p className="text-neutral-primary text-lg font-semibold">{formatAmount(totals.quote)}</p>
          <p className="text-neutral-secondary-mid text-[10px]">
            Offertes met status "Goedgekeurd", excl. btw
          </p>
        </div>
        <div>
          <p className="text-neutral-secondary-mid text-xs uppercase">Factuurbedrag</p>
          <p
            className={`text-lg font-semibold ${totals.invoice < 0 ? 'text-red-600' : 'text-neutral-primary'}`}
          >
            {formatAmount(totals.invoice)}
          </p>
          <p className="text-neutral-secondary-mid text-[10px]">
            Gekoppelde facturen, creditnota's afgetrokken
          </p>
        </div>
      </div>

      {isPickerOpen && (
        <div className="border-neutral-primary/10 border-b bg-white px-4 py-3">
          <div className="relative">
            <MagnifyingGlass
              size={14}
              className="text-neutral-secondary-mid absolute top-1/2 left-2.5 -translate-y-1/2"
            />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Zoek op documentnummer of leverancier..."
              className="h-8 pl-8 text-xs"
            />
          </div>
          {/* Wat je nu ziet: zonder zoekterm de documenten van het gebouw van het
              ticket, mét zoekterm alle vrije documenten. Dat verschil expliciet
              benoemen — anders lijkt een lege lijst op "er is niets". */}
          <p className="text-neutral-secondary-mid mt-1 text-[10px]">
            {search.trim()
              ? "Zoekt in alle documenten die nog aan geen ticket hangen."
              : "Documenten van het gebouw van dit ticket die nog aan geen ticket hangen — typ om in álle documenten te zoeken."}
          </p>
          <div className="mt-2 max-h-64 space-y-1 overflow-y-auto">
            {isSearching ? (
              <div className="flex items-center gap-2 px-1 py-3 text-xs text-neutral-secondary-mid">
                <Spinner className="h-3 w-3" /> Zoeken...
              </div>
            ) : candidates.length === 0 ? (
              <p className="text-neutral-secondary-mid px-1 py-3 text-xs">
                Geen documenten gevonden om te koppelen.
              </p>
            ) : (
              candidates.map((doc) => (
                <button
                  key={doc.id}
                  onClick={() => link(doc.id)}
                  disabled={busyId === doc.id}
                  className="hover:bg-neutral-primary/5 flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs disabled:opacity-50"
                >
                  <Plus size={12} className="text-moss-primary shrink-0" />
                  <span className="min-w-0 flex-1 truncate">
                    <span className="text-neutral-primary font-medium">{doc.documentNumber}</span>
                    <span className="text-neutral-secondary-mid">
                      {' · '}
                      {doc.type} · {doc.supplier?.name ?? 'geen leverancier'} ·{' '}
                      {formatDate(doc.documentDate)}
                    </span>
                  </span>
                  <span className="text-neutral-primary shrink-0 font-medium">
                    {formatAmount(renoDocumentAmount(doc))}
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      )}

      <div className="divide-neutral-primary/10 divide-y">
        {isLoading ? (
          <div className="flex items-center gap-2 px-4 py-6 text-xs text-neutral-secondary-mid">
            <Spinner className="h-3 w-3" /> Documenten laden...
          </div>
        ) : documents.length === 0 ? (
          <p className="text-neutral-secondary-mid px-4 py-6 text-sm">
            Nog geen offerte, factuur of bestelbon gekoppeld aan dit ticket.
          </p>
        ) : (
          documents.map((doc) => {
            const amount = renoDocumentAmount(doc);
            return (
              <div key={doc.id} className="flex items-center gap-3 px-4 py-2">
                <span
                  className={`shrink-0 rounded border px-1.5 py-0.5 text-[10px] font-medium ${
                    TYPE_BADGE[doc.type] ?? 'border-gray-200 bg-gray-50 text-gray-700'
                  }`}
                >
                  {doc.type}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-neutral-primary truncate text-sm font-medium">
                    {doc.documentNumber}
                    {doc.isCreditNote && (
                      <span className="ml-1.5 rounded border border-red-200 bg-red-50 px-1 py-0.5 text-[10px] text-red-700">
                        Creditnota
                      </span>
                    )}
                  </p>
                  <p className="text-neutral-secondary-mid truncate text-[11px]">
                    {doc.status} · {doc.supplier?.name ?? 'geen leverancier'} ·{' '}
                    {formatDate(doc.documentDate)}
                  </p>
                </div>
                <span
                  className={`shrink-0 text-sm font-semibold ${amount < 0 ? 'text-red-600' : 'text-neutral-primary'}`}
                >
                  {formatAmount(amount)}
                </span>
                <button
                  onClick={() => unlink(doc.id)}
                  disabled={busyId === doc.id}
                  title="Koppeling losmaken (het document zelf blijft bestaan)"
                  className="shrink-0 rounded-md bg-gray-100 p-1 text-gray-600 transition-all hover:bg-red-600 hover:text-white disabled:opacity-50"
                >
                  <X size={12} />
                </button>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default RenoFinancialDocumentsSection;
